import React, { useState } from "react";
import {
  FiSearch,
  FiCalendar,
  FiUsers,
  FiPackage,
  FiCheckCircle,
  FiRotateCcw,
  FiClock,
  FiPhone,
  FiMapPin,
} from "react-icons/fi";

import {
  getRepresentativesByDeliveryRange,
} from "./RepresentativeCommissionService";

const RepresentativeDeliveryRange = () => {
  const [minRate, setMinRate] = useState("50");
  const [maxRate, setMaxRate] = useState("65");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [representatives, setRepresentatives] = useState([]);
  const [totalRepresentatives, setTotalRepresentatives] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* =========================================================
     SEARCH
  ========================================================= */

  const handleSearch = async (event) => {
    event.preventDefault();

    setError("");

    const min = Number(minRate);
    const max = Number(maxRate);

    if (minRate === "" || maxRate === "") {
      setError("من فضلك حدد نسبة التسليم");
      return;
    }

    if (min < 0 || min > 100 || max < 0 || max > 100) {
      setError("نسبة التسليم يجب أن تكون بين 0 و 100");
      return;
    }

    if (min > max) {
      setError(
        "النسبة الأولى يجب أن تكون أقل من أو تساوي النسبة الثانية"
      );
      return;
    }

    if (fromDate && toDate && fromDate > toDate) {
      setError(
        "تاريخ البداية يجب أن يكون قبل تاريخ النهاية"
      );
      return;
    }

    try {
      setLoading(true);

      const response =
        await getRepresentativesByDeliveryRange({
          minRate: min,
          maxRate: max,
          fromDate,
          toDate,
        });

      const data = response?.data ?? response;

      setRepresentatives(
        Array.isArray(data?.representatives)
          ? data.representatives
          : []
      );

      setTotalRepresentatives(
        data?.totalRepresentatives ??
          data?.representatives?.length ??
          0
      );
    } catch (error) {
      console.error(error);

      setRepresentatives([]);
      setTotalRepresentatives(0);

      setError(
        error?.response?.data?.message ||
          "حدث خطأ أثناء جلب المندوبين"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     RATE CLASS
  ========================================================= */

  const getRateClass = (rate) => {
    if (rate >= 80) return "rate-high";
    if (rate >= 60) return "rate-medium";
    return "rate-low";
  };

  return (
    <div className="delivery-range-section">

      {/* ================= FILTER CARD ================= */}

      <div className="range-filter-card">

        <div className="section-title">

          <div className="section-title-icon">
            <FiUsers size={18} />
          </div>

          <div>
            <h3>البحث حسب نسبة التسليم</h3>

            <p>
              حدد نسبة التسليم والفترة الزمنية لعرض المندوبين
            </p>
          </div>

        </div>

        <form
          className="range-filter-form"
          onSubmit={handleSearch}
        >

          {/* ================= MIN RATE ================= */}

          <div className="form-field">

            <label>
              من نسبة
            </label>

            <div className="percentage-input">

              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={minRate}
                onChange={(event) =>
                  setMinRate(event.target.value)
                }
              />

              <span>%</span>

            </div>

          </div>

          {/* ================= MAX RATE ================= */}

          <div className="form-field">

            <label>
              إلى نسبة
            </label>

            <div className="percentage-input">

              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={maxRate}
                onChange={(event) =>
                  setMaxRate(event.target.value)
                }
              />

              <span>%</span>

            </div>

          </div>

          {/* ================= FROM DATE ================= */}

          <div className="form-field">

            <label>
              من تاريخ
            </label>

            <div className="input-with-icon">

              <FiCalendar size={17} />

              <input
                type="date"
                value={fromDate}
                onChange={(event) =>
                  setFromDate(event.target.value)
                }
              />

            </div>

          </div>

          {/* ================= TO DATE ================= */}

          <div className="form-field">

            <label>
              إلى تاريخ
            </label>

            <div className="input-with-icon">

              <FiCalendar size={17} />

              <input
                type="date"
                value={toDate}
                onChange={(event) =>
                  setToDate(event.target.value)
                }
              />

            </div>

          </div>

          {/* ================= BUTTON ================= */}

          <button
            type="submit"
            className="commission-search-button"
            disabled={loading}
          >

            <FiSearch size={17} />

            <span>
              {loading ? "جاري البحث..." : "بحث"}
            </span>

          </button>

        </form>

        {error && (
          <div className="commission-error">
            {error}
          </div>
        )}

      </div>

      {/* ================= RESULT HEADER ================= */}

      {representatives.length > 0 && (
        <div className="range-result-header">

          <div>
            <h3>
              المندوبين
            </h3>

            <p>
              عدد النتائج:{" "}
              <strong>
                {totalRepresentatives}
              </strong>
            </p>
          </div>

          <div className="range-result-badge">
            {minRate}% - {maxRate}%
          </div>

        </div>
      )}

      {/* ================= TABLE ================= */}

      {representatives.length > 0 && (
        <div className="representatives-table-wrapper">

          <table className="representatives-table">

            <thead>
              <tr>
                <th>#</th>
                <th>المندوب</th>
                <th>الهاتف</th>
                <th>المحافظة</th>
                <th>الطلبات</th>
                <th>المسلم</th>
                <th>المرتجع</th>
                <th>المعلق</th>
                <th>نسبة التسليم</th>
              </tr>
            </thead>

            <tbody>

              {representatives.map(
                (representative, index) => (
                  <tr
                    key={
                      representative.representativeId ??
                      index
                    }
                  >

                    <td>
                      {index + 1}
                    </td>

                    <td>

                      <div className="representative-table-name">

                        <div className="small-user-icon">
                          <FiUsers size={15} />
                        </div>

                        <span>
                          {
                            representative.representativeName ||
                            "-"
                          }
                        </span>

                      </div>

                    </td>

                    <td>

                      <div className="table-icon-text">
                        <FiPhone size={14} />

                        <span>
                          {representative.phoneNumber || "-"}
                        </span>
                      </div>

                    </td>

                    <td>

                      <div className="table-icon-text">
                        <FiMapPin size={14} />

                        <span>
                          {
                            representative.governorateName ||
                            "-"
                          }
                        </span>
                      </div>

                    </td>

                    <td>
                      <div className="table-number">
                        <FiPackage size={14} />
                        {representative.totalOrders ?? 0}
                      </div>
                    </td>

                    <td>
                      <div className="table-number">
                        <FiCheckCircle size={14} />
                        {representative.deliveredOrders ?? 0}
                      </div>
                    </td>

                    <td>
                      <div className="table-number">
                        <FiRotateCcw size={14} />
                        {representative.returnedOrders ?? 0}
                      </div>
                    </td>

                    <td>
                      <div className="table-number">
                        <FiClock size={14} />
                        {representative.pendingOrders ?? 0}
                      </div>
                    </td>

                    <td>

                      <span
                        className={`delivery-rate-badge ${getRateClass(
                          representative.deliveryRate
                        )}`}
                      >
                        {representative.deliveryRate ?? 0}%
                      </span>

                    </td>

                  </tr>
                )
              )}

            </tbody>

          </table>

        </div>
      )}

      {/* ================= EMPTY ================= */}

      {!loading && representatives.length === 0 && !error && (
        <div className="empty-range-state">

          <div className="empty-range-icon">
            <FiUsers size={23} />
          </div>

          <h3>
            لا توجد نتائج
          </h3>

          <p>
            حدد نسبة التسليم والتاريخ ثم اضغط بحث
          </p>

        </div>
      )}

    </div>
  );
};

export default RepresentativeDeliveryRange;